import { Activity, ActivityStates } from '../../types/activities'
import { Answer, AnswerResults, Question, QuestionId } from '../../types/questions'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { QuestionItem } from './question-item/QuestionItem'
import { RegistrationCheckContext } from '../../providers/registration-check-provider'

type Props = {
    activity: Activity
    canBeDeleted: boolean
    deleteActivity: () => void
    updateActivity: (activity: Activity) => void
}

/**
 * The component that contains everything concerning one single activity flow
 * Can contain multiple 'QuestionItems'
 */
export const ActivityItem = ({ activity, canBeDeleted, updateActivity, deleteActivity }: Props): ReactElement => {
    const { questions } = useContext(RegistrationCheckContext)

    const { control, watch, getValues } = useForm()

    const allValues = watch()

    const [visibleQuestions, setVisibleQuestions] = useState<QuestionId[]>([questions[0].id])

    /** Gets the "questionId" from the "formDataKey"
     * @param {string} formDataKey String of format "ACactivityId_questionId"
     */
    const getQuestionIdFromFormDataKey = (formDataKey: string): string => formDataKey.split('_')[1]

    /**
     * Updates the list of "VisibleQuestions" based on given answer(s).
     * @param {string | undefined} lastChangedFormValue The key of the last changed form data, string of format "activityId-questionId"
     */
    const setNewVisibleQuestions = (lastChangedFormValue: string | undefined): void => {
        const formData = getValues()
        const newVisibleQuestions = [questions[0].id]

        Object.entries(formData).forEach(([formDataKey, givenAnswers]) => {
            /** If givenAnswerId is not an ID, it can be ignored */
            if (!Array.isArray(givenAnswers) && ['', null, undefined].includes(givenAnswers)) return
            if (
                Array.isArray(givenAnswers) &&
                givenAnswers.filter((answer) => !['', null, undefined].includes(answer)).length <= 0
            )
                return

            /** If lastChangedForm value is an earlier question in the list, it can be ignored */
            if (
                lastChangedFormValue &&
                questions.findIndex((question) => question.id === getQuestionIdFromFormDataKey(lastChangedFormValue)) <
                    questions.findIndex((question) => question.id === getQuestionIdFromFormDataKey(formDataKey))
            )
                return

            const questionObject = questions.find(
                (question) => question.id === getQuestionIdFromFormDataKey(formDataKey),
            )
            /** If there is no matching Question, it can be ignored */
            if (!questionObject) return

            const answerObjects = questionObject.questionContent.answers.filter((answer) => {
                if (answer.result.type !== AnswerResults.SHOW_NEXT_QUESTION || !answer.result.target) {
                    return false
                }
                if (Array.isArray(givenAnswers)) {
                    return givenAnswers.includes(answer.id)
                } else {
                    return answer.id === givenAnswers
                }
            })

            /** If there is no matching Answer, the answer should not show a next question or it has no target question, it can be ignored */
            if (answerObjects.length <= 0) return

            newVisibleQuestions.push(...answerObjects.map((answer) => answer.result.target))
        })

        setVisibleQuestions(newVisibleQuestions)
    }

    const handleUpdateActivity = (): void => {
        const formData = getValues()
        const newActivity = activity
        /** Set new empty arrays of RegistrationKeys and AnswerResults */
        const keys: string[] = []
        const nonContractKeys: string[] = []
        const results: AnswerResults[] = []

        /** Get formEntries as iterable array from formData */
        const formEntries: [string, string | string[] | null | undefined][] = Object.entries(formData)
        if (formEntries.length > 0) {
            /** Go through each formEntry to check if a RegistrationKey or AnswerResult should be registered */
            formEntries.forEach(([formDataKey, givenAnswers]) => {
                const questionId = getQuestionIdFromFormDataKey(formDataKey)
                const question = questions.find((question) => question.id === questionId)
                const answers: Answer[] = question?.questionContent.answers.filter((answer) => {
                    /** Because of checkbox-questions and multiselect-questions, a givenAnswer CAN be an array of answerIds */
                    if (Array.isArray(givenAnswers)) {
                        return givenAnswers.includes(answer.id)
                    } else {
                        return answer.id === givenAnswers
                    }
                })

                /** For each given answer we have to check/push the registrationKey and result */
                answers.forEach((answer) => {
                    const key = answer?.result?.registrationKey
                    const nonContractRegistrationKey = answer?.result?.nonContractRegistrationKey

                    if (key) {
                        keys.push(key)
                    }

                    if (nonContractRegistrationKey) {
                        nonContractKeys.push(nonContractRegistrationKey)
                    }

                    results.push(answer?.result?.type)
                })
            })
            /** Remove duplicates from keys */
            const uniqueKeys = [...new Set(keys)]
            const uniqueNonContractKeys = [...new Set(nonContractKeys)]
            /** Update activity with new registrationKeys and answerResults */
            newActivity.answerResults = results
            newActivity.registrationKeys = uniqueKeys
            newActivity.nonContractRegistrationKeys = uniqueNonContractKeys
        }

        const formDataArray = Object.values(formData).map((value) =>
            Array.isArray(value) && value.length === 0 ? undefined : value,
        )

        newActivity.status =
            formDataArray.includes(undefined) || formDataArray.includes([]) || formDataArray.length === 0
                ? ActivityStates.IN_PROGRESS
                : ActivityStates.FINISHED

        updateActivity(newActivity)
    }

    useEffect(() => {
        const watchAllFields = watch((_, { name }) => {
            setNewVisibleQuestions(name)
            handleUpdateActivity()
        })

        return () => watchAllFields.unsubscribe()
    }, [watch, setNewVisibleQuestions])

    return (
        <>
            {questions.map((question: Question, index: number) => (
                <QuestionItem
                    key={question.id}
                    question={question}
                    questions={questions}
                    activity={activity}
                    visibleQuestions={visibleQuestions}
                    control={control}
                    formData={allValues}
                    canBeDeleted={canBeDeleted && index === 0}
                    deleteActivity={deleteActivity}
                    updateActivity={() => {
                        handleUpdateActivity()
                    }}
                />
            ))}
        </>
    )
}
